/**
 * Inaktiv-Reaktivierung — Kunden ohne Werkstattbesuch seit N Monaten gezielt
 * reaktivieren: Gutschein-Kampagne (E-Mail-Einwilligung), Einwilligung einholen
 * (E-Mail/SMS/WhatsApp, Double-Opt-in) und Termin-Angebot (SMS/WhatsApp).
 * Versand ist gemockt, solange keine Resend-/Twilio-Live-Keys gesetzt sind.
 */
import { useState } from "react";
import { HeartPulse, Loader2, Mail, MessageSquare, CalendarPlus, ShieldCheck, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import api from "../../lib/api";
import { toast } from "sonner";

const fmtN = (n) => Number(n || 0).toLocaleString("de-DE");

export function ReactivationCard() {
    const [open, setOpen] = useState(false);
    const [months, setMonths] = useState("12");
    const [pct, setPct] = useState(15);
    const [validDays, setValidDays] = useState(30);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [modal, setModal] = useState(null); // "campaign" | "consent" | "appointment"

    const evaluate = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/reactivation/segment", {
                params: { inactive_months: Number(months) },
            });
            setStats(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auswertung fehlgeschlagen");
        } finally { setLoading(false); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="reactivation-card">
            <button type="button" onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 p-4 text-left" data-testid="reactivation-toggle">
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <HeartPulse className="w-4 h-4 text-rose-500" />
                <h3 className="font-bold text-sm">Inaktiv-Reaktivierung</h3>
                <span className="text-[11px] text-gray-400 hidden sm:inline">— Kunden ohne Werkstattbesuch seit … gezielt mit Gutschein zurückholen</span>
            </button>
            {open && (
            <div className="px-4 pb-4">

            <div className="flex flex-wrap items-end gap-2">
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Inaktiv seit</span>
                    <Select value={months} onValueChange={(v) => { setMonths(v); setStats(null); }}>
                        <SelectTrigger className="h-9 w-[130px] rounded-sm mt-1" data-testid="reactivation-months-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="12">12 Monaten</SelectItem>
                            <SelectItem value="18">18 Monaten</SelectItem>
                            <SelectItem value="24">24 Monaten</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gutschein %</span>
                    <Input type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} className="h-9 w-20 rounded-sm mt-1" data-testid="reactivation-pct" />
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gültig (Tage)</span>
                    <Input type="number" min={1} max={365} value={validDays} onChange={(e) => setValidDays(e.target.value)} className="h-9 w-20 rounded-sm mt-1" data-testid="reactivation-days" />
                </label>
                <Button onClick={evaluate} disabled={loading} className="h-9 rounded-sm font-bold bg-rose-600 hover:bg-rose-700 text-white" data-testid="reactivation-eval-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Auswerten
                </Button>
            </div>

            {stats && (
                <div className="mt-4 space-y-3" data-testid="reactivation-stats">
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 rounded-sm bg-rose-50 border border-rose-200 text-rose-800"><b>{fmtN(stats.total)}</b> inaktive Kunden</span>
                        <span className="px-2 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800"><b>{fmtN(stats.with_email_consent)}</b> mit E-Mail-Einwilligung</span>
                        <span className="px-2 py-1 rounded-sm bg-amber-50 border border-amber-200 text-amber-800"><b>{fmtN(stats.without_consent)}</b> ohne Einwilligung</span>
                        <span className="px-2 py-1 rounded-sm bg-sky-50 border border-sky-200 text-sky-800"><b>{fmtN(stats.reachable_phone)}</b> per Telefon erreichbar</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={() => setModal("campaign")} disabled={!stats.with_email_consent} className="h-9 rounded-sm font-bold bg-[#111827] hover:bg-black text-white" data-testid="reactivation-campaign-btn">
                            <Mail className="w-4 h-4 mr-1.5" /> Gutschein-Kampagne ({fmtN(stats.with_email_consent)})
                        </Button>
                        <Button onClick={() => setModal("consent")} disabled={!stats.without_consent} variant="outline" className="h-9 rounded-sm font-bold border-amber-500 text-amber-700" data-testid="reactivation-consent-btn">
                            <ShieldCheck className="w-4 h-4 mr-1.5" /> Einwilligung einholen ({fmtN(stats.without_consent)})
                        </Button>
                        <Button onClick={() => setModal("appointment")} disabled={!stats.reachable_phone} variant="outline" className="h-9 rounded-sm font-bold border-sky-500 text-sky-700" data-testid="reactivation-appointment-btn">
                            <CalendarPlus className="w-4 h-4 mr-1.5" /> Termin anbieten ({fmtN(stats.reachable_phone)})
                        </Button>
                    </div>
                </div>
            )}

            {modal === "campaign" && (
                <CampaignModal months={Number(months)} pct={Number(pct)} validDays={Number(validDays)} onClose={() => setModal(null)} />
            )}
            {modal === "consent" && (
                <ConsentModal months={Number(months)} onClose={() => setModal(null)} />
            )}
            {modal === "appointment" && (
                <AppointmentModal months={Number(months)} onClose={() => setModal(null)} />
            )}
            </div>
            )}
        </div>
    );
}

function ModalShell({ title, icon, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="reactivation-modal">
            <div className="bg-white rounded-sm w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-bold text-base flex items-center gap-2">{icon}{title}</h3>
                    <button onClick={onClose} data-testid="reactivation-modal-close"><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function useCampaignPoll() {
    const [result, setResult] = useState(null);
    const poll = async (id) => {
        try {
            const { data } = await api.get(`/dealer/reactivation/campaign/${id}/status`);
            setResult(data);
            if (data.status === "done" || data.status === "error") return;
            setTimeout(() => poll(id), 1200);
        } catch { setTimeout(() => poll(id), 2000); }
    };
    return [result, poll, setResult];
}

function CampaignModal({ months, pct, validDays, onClose }) {
    const [subject, setSubject] = useState("Wir vermissen Sie – {gutschein_prozent}% Gutschein für Ihren Service");
    const [body, setBody] = useState("Hallo {anrede} {nachname},\n\nIhr letzter Werkstattbesuch ist eine Weile her. Als Dankeschön schenken wir Ihnen {gutschein_prozent}% auf Ihren nächsten Service.\n\nIhr Gutschein-Code: {gutschein_code} (gültig bis {gutschein_gueltig_bis}).\n\nWir freuen uns auf Sie!");
    const [sending, setSending] = useState(false);
    const [result, poll] = useCampaignPoll();

    const send = async () => {
        if (!subject.trim() || !body.trim()) { toast.error("Betreff und Text erforderlich"); return; }
        setSending(true);
        try {
            const { data } = await api.post("/dealer/reactivation/campaign", {
                inactive_months: months, discount_pct: pct, valid_days: validDays, subject, body,
            });
            toast.success(`${data.recipients} Empfänger – Versand gestartet${data.mocked ? " (Test-Modus)" : ""}`);
            poll(data.campaign_id);
        } catch (e) { setSending(false); toast.error(e?.response?.data?.detail || "Start fehlgeschlagen"); }
    };

    return (
        <ModalShell title="Gutschein-Reaktivierungs-Kampagne" icon={<Mail className="w-5 h-5 text-[#111827]" />} onClose={onClose}>
            <p className="text-xs text-gray-500">Nur an inaktive Kunden mit E-Mail-Einwilligung. Pro Empfänger wird automatisch ein {pct}% Gutschein erzeugt.</p>
            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betreff</span>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 rounded-sm" data-testid="reactivation-campaign-subject" />
            </label>
            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht</span>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="mt-1 rounded-sm" data-testid="reactivation-campaign-body" />
            </label>
            <div className="text-[11px] text-gray-400">Platzhalter: <code>{"{anrede}"}</code> <code>{"{nachname}"}</code> <code>{"{gutschein_code}"}</code> <code>{"{gutschein_prozent}"}</code> <code>{"{gutschein_gueltig_bis}"}</code></div>
            {result ? (
                <div className="text-sm bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="reactivation-campaign-result">
                    Status: <b>{result.status}</b> · {fmtN(result.sent)}/{fmtN(result.total)} versendet{result.mocked ? " (Test-Modus)" : ""}
                </div>
            ) : (
                <Button onClick={send} disabled={sending} className="rounded-sm font-bold bg-[#111827] hover:bg-black text-white w-full" data-testid="reactivation-campaign-send">
                    {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />} Kampagne senden
                </Button>
            )}
        </ModalShell>
    );
}

function ChannelToggle({ value, onChange, options }) {
    const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => (
                <button key={o.k} type="button" onClick={() => toggle(o.k)}
                    className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${value.includes(o.k) ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300"}`}
                    data-testid={`reactivation-channel-${o.k}`}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function ConsentModal({ months, onClose }) {
    const [channels, setChannels] = useState(["email"]);
    const [message, setMessage] = useState("Hallo {name}, dürfen wir Sie zu Service-Aktionen & Gutscheinen kontaktieren? Bitte hier zustimmen:");
    const [sending, setSending] = useState(false);
    const [result, poll] = useCampaignPoll();

    const send = async () => {
        if (!channels.length) { toast.error("Mindestens einen Kanal wählen"); return; }
        setSending(true);
        try {
            const { data } = await api.post("/dealer/reactivation/consent-request", {
                inactive_months: months, channels, message, base_url: window.location.origin,
            });
            toast.success(`${data.recipients} Anfragen gestartet (${data.channels.join(", ")})`);
            poll(data.campaign_id);
        } catch (e) { setSending(false); toast.error(e?.response?.data?.detail || "Start fehlgeschlagen"); }
    };

    return (
        <ModalShell title="Einwilligung einholen (Double-Opt-in)" icon={<ShieldCheck className="w-5 h-5 text-amber-600" />} onClose={onClose}>
            <p className="text-xs text-gray-500">An inaktive Kunden OHNE E-Mail-Einwilligung. Empfänger erhalten einen Bestätigungslink; erst nach Klick wird die Einwilligung gesetzt (DSGVO-konform).</p>
            <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Kanäle</span>
                <ChannelToggle value={channels} onChange={setChannels} options={[
                    { k: "email", label: "E-Mail" }, { k: "sms", label: "SMS" }, { k: "whatsapp", label: "WhatsApp" },
                ]} />
            </div>
            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht</span>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1 rounded-sm" data-testid="reactivation-consent-message" />
            </label>
            <div className="text-[11px] text-gray-400">Platzhalter: <code>{"{name}"}</code> · Der Bestätigungslink wird automatisch angehängt.</div>
            {result ? (
                <div className="text-sm bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="reactivation-consent-result">
                    Status: <b>{result.status}</b> · {fmtN(result.sent)}/{fmtN(result.total)} gesendet{result.mocked ? " (Test-Modus)" : ""}
                </div>
            ) : (
                <Button onClick={send} disabled={sending} className="rounded-sm font-bold bg-amber-600 hover:bg-amber-700 text-white w-full" data-testid="reactivation-consent-send">
                    {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />} Anfragen senden
                </Button>
            )}
        </ModalShell>
    );
}

function AppointmentModal({ months, onClose }) {
    const [channels, setChannels] = useState(["sms"]);
    const [message, setMessage] = useState("Hallo {name}, Zeit für Service oder HU? Sichern Sie sich jetzt einen Termin bei uns:");
    const [bookingUrl, setBookingUrl] = useState("");
    const [sending, setSending] = useState(false);
    const [result, poll] = useCampaignPoll();

    const send = async () => {
        if (!channels.length) { toast.error("Mindestens einen Kanal wählen"); return; }
        setSending(true);
        try {
            const { data } = await api.post("/dealer/reactivation/appointment-offer", {
                inactive_months: months, channels, message, booking_url: bookingUrl,
            });
            toast.success(`${data.recipients} Termin-Angebote gestartet (${data.channels.join(", ")})`);
            poll(data.campaign_id);
        } catch (e) { setSending(false); toast.error(e?.response?.data?.detail || "Start fehlgeschlagen"); }
    };

    return (
        <ModalShell title="Termin anbieten (SMS / WhatsApp)" icon={<CalendarPlus className="w-5 h-5 text-sky-600" />} onClose={onClose}>
            <p className="text-xs text-gray-500">An inaktive Kunden mit Telefon/Mobil. Versand per SMS und/oder WhatsApp.</p>
            <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Kanäle</span>
                <ChannelToggle value={channels} onChange={setChannels} options={[
                    { k: "sms", label: "SMS" }, { k: "whatsapp", label: "WhatsApp" },
                ]} />
            </div>
            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht</span>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1 rounded-sm" data-testid="reactivation-appointment-message" />
            </label>
            <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Buchungs-Link (optional)</span>
                <Input value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://…/termin" className="mt-1 rounded-sm" data-testid="reactivation-appointment-url" />
            </label>
            {result ? (
                <div className="text-sm bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="reactivation-appointment-result">
                    Status: <b>{result.status}</b> · {fmtN(result.sent)}/{fmtN(result.total)} gesendet{result.mocked ? " (Test-Modus)" : ""}
                </div>
            ) : (
                <Button onClick={send} disabled={sending} className="rounded-sm font-bold bg-sky-600 hover:bg-sky-700 text-white w-full" data-testid="reactivation-appointment-send">
                    {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1.5" />} Angebote senden
                </Button>
            )}
        </ModalShell>
    );
}
